import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { GENESIS_TERMINAL_PART_UID, isGroundElement, PcbNodeTypes, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
import { applyChange } from "deep-diff";
import { omit } from "lodash";
import { create } from "mutative";
import { SymbolLayout } from "../../elements";
import { ElementHelper } from "../../elements/ElementHelper";
import { DocumentReducerError } from "../../flux_error";
import { filterTruthy } from "../../helpers";
import { removeNodesAndDescendants, setElementsAndGenerateNodes } from "../../nodes";
import { PcbLayoutRuleCompiler, PcbTreeManager } from "../../pcb";
import { getProperty } from "../../property";
import { removeSubjectsFromSelection, updateSelectedSubjects } from "./helpers";
/**
 * Handles the `updateDescription` redux action by mutating document's description
 *
 * TODO: Think we should combine this with the mutators used to handle command... Will think about this when cleaning up
 * the `applyCommand` action later
 */
export function handleUpdateDescription(draftState, description) {
    draftState.description = description;
}
/**
 * Handles the `updateElementsLabel` redux action by mutating an element's
 * label
 */
export function handleUpdateElementsLabel(draftState, elements, logger) {
    for (const updatedElement of elements) {
        const element = draftState.elements[updatedElement.uid];
        if (element) {
            element.label = updatedElement.label;
            element.updated_at = new Date().getTime();
        }
        const node = draftState.pcbLayoutNodes[updatedElement.uid];
        if (node) {
            node.name = updatedElement.label || "";
        }
    }
    const hasTerminals = elements.some((el) => el.part_uid === GENESIS_TERMINAL_PART_UID);
    const hasNetPortal = elements.some((el) => ElementHelper.isPortal(el));
    // We update the pcb layout nodes only if we have terminals or net portal so that we cover the case
    // where a portal has been renamed by the user.
    if (hasTerminals || hasNetPortal) {
        setElementsAndGenerateNodes(draftState, Object.values(elements));
        const pcbTreeManager = new PcbTreeManager(draftState.pcbLayoutNodes, logger);
        pcbTreeManager.updateNetNodes(draftState);
        draftState.pcbLayoutNodes = pcbTreeManager.treeNodes;
    }
}
export function handleAddElements(draftState, data) {
    const { elements, selectElements, inPcbEditor, dropPosition, genesisContainerType } = data;
    // NOTE: Historically, part documents were created that included
    // themselves for a kind of "preview" of the published part. For
    // snapshots, what we want generally is a DAG, but that is not enforced
    // here, and it is likely very rare to have cycles beyond
    // self-inclusion. For more discussion, see
    // [workplace-post/340449055242166](/docs/workplace/340449055242166.md)
    // See yieldAllSubDocuments.
    if (elements.some((element) => {
        const { document_import_uid, part_uid } = element.part_version_data_cache;
        return document_import_uid === draftState.uid || (part_uid && part_uid === draftState.belongs_to_part_uid);
    })) {
        throw new DocumentReducerError("addElements", "Cannot add a part to the document that it was published from", {});
    }
    setElementsAndGenerateNodes(draftState, elements, { inPcbEditor, dropPosition, genesisContainerType });
    const symbolUids = elements.flatMap((e) => SymbolLayout.getSymbolInfo(e).map((s) => ElementHelper.makeSymbolUid(e.uid, s.name)));
    if (selectElements) {
        updateSelectedSubjects(draftState, [...elements.map((element) => element.uid), ...symbolUids]);
    }
}
export function handleRemoveSubjectProperties(draftState, propertyRemovalMap, logger) {
    const deletePropertyIfRemovable = (properties, propertyKey) => {
        const property = properties[propertyKey];
        if (property && !property.isPreset) {
            delete properties[propertyKey];
        }
    };
    for (const [subjectUid, { type: subjectType, propertyKeysToRemove: removePropertyKeys }] of Object.entries(propertyRemovalMap)) {
        const subjectProperties = draftState.elements[subjectUid]?.properties;
        const wasExcludedFromPcb = subjectProperties && ElementHelper.isExcludedFromPcb(subjectProperties);
        for (const propertyKey of removePropertyKeys) {
            if (subjectType === "elements" && draftState.elements) {
                if (propertyKey in draftState.elements[subjectUid].properties) {
                    deletePropertyIfRemovable(draftState.elements[subjectUid].properties, propertyKey);
                }
                else {
                    // During batch-edit, properties are identified by their name,
                    // not uid. Delete properties by name instead. Make sure we
                    // delete *all* properties with the same name, because there
                    // may be hidden duplicates, which our code doesn't handle
                    // very well (and end up being even more confusing when they
                    // don't delete properly!)
                    const existingProperties = Object.values(draftState.elements[subjectUid].properties).filter((prop) => prop.name === propertyKey);
                    for (const property of existingProperties) {
                        deletePropertyIfRemovable(draftState.elements[subjectUid].properties, property.uid);
                    }
                }
            }
            else if (subjectType === "routes" && draftState.routes) {
                deletePropertyIfRemovable(draftState.routes[subjectUid].properties, propertyKey);
            }
            else if (subjectType === "nets" && draftState.nets) {
                deletePropertyIfRemovable(draftState.nets[subjectUid].properties, propertyKey);
            }
        }
        // TODO: This block could be a good candidate of domain function
        // Start special case exclude_from_pcb element property
        const isExcludedFromPcb = subjectType === "elements" && ElementHelper.isExcludedFromPcb(draftState.elements[subjectUid].properties);
        if (subjectType === "elements" && wasExcludedFromPcb === true && isExcludedFromPcb === undefined) {
            if (subjectType === "elements") {
                const element = draftState.elements[subjectUid];
                if (element) {
                    setElementsAndGenerateNodes(draftState, [element]);
                }
            }
            // QUESTION: how to add only the nodes from the
            // subjectUid? or do we need this in case the element is
            // a layout or footprint?
            new PcbTreeManager(draftState.pcbLayoutNodes, logger).updateNetNodes(draftState);
        }
        else if (subjectType === "nets" && draftState.nets) {
            new PcbTreeManager(draftState.pcbLayoutNodes, logger).updateNetNodes(draftState);
        }
    }
    // End special case exclude_from_pcb element property
}
export function handleSetSubjectProperties(draftState, logger, propertyUpdateMap, 
// TODO: This parameter is not used at all??
_subscriptionKey) {
    for (const [subjectUid, { subjectType, propertyUpdates }] of Object.entries(propertyUpdateMap)) {
        const subject = subjectType === "elements"
            ? draftState.elements?.[subjectUid]
            : subjectType === "routes"
                ? draftState.routes?.[subjectUid]
                : subjectType === "nets"
                    ? draftState.nets?.[subjectUid]
                    : undefined;
        if (!subject) {
            throw new DocumentReducerError("setSubjectProperties", `Can't set subject properties since subject ${subjectUid} does not exist in ${subjectType} collection`, 
            // NOTE: we used to pass the `setSubjectProperties` action here, which doesn't seem right
            // as it expects a `reducerFunction` here... we just pass an empty object here to simplify things
            {});
        }
        const isBatchEdit = Object.values(propertyUpdateMap).length > 1;
        for (const setProperty of Object.values(propertyUpdates)) {
            if (isBatchEdit) {
                // If updating properties of multiple objects (eg for batch property
                // edit), we need to update the existing property, not add a new
                // property -- the provided property is likely a different subject's
                // property, and adding it to this subject will cause it to have a
                // duplicate entry for that property. For some default properties
                // like part_type, this matters less, because the key "part_type" is
                // used as the uid, and so only one entry can exist. But many
                // properties (like "section") have unique uids, and end up with
                // multiple entries during batch edit.
                const updateExisting = getProperty(setProperty.name, subject.properties);
                // Ensure property.state doesn't get saved to the document.
                const updatedProperty = {
                    ...omit(setProperty, "state"),
                    // If property doesn't exist, add it (eg for when the user adds a
                    // property via the property dialog)
                    uid: updateExisting?.uid ?? guid(),
                };
                subject.properties[updatedProperty.uid] = updatedProperty;
            }
            else {
                if (!("uid" in setProperty)) {
                    throw new Error("Single-subject properties must have a uid");
                }
                subject.properties[setProperty.uid] = setProperty;
            }
        }
        const propertiesRequiringNodeRegenerate = [
            defaultPropertyDefinitions.exclude_from_pcb.label,
            defaultPropertyDefinitions.pin_number.label,
        ];
        const nodeRegenerateRequired = Object.values(propertyUpdates).some((p) => propertiesRequiringNodeRegenerate.includes(p.name));
        // setElementsAndGenerateNodes() etc is expensive, so don't execute it for most property
        // changes -- only ones that affect the nodes, like pin_number and exclude_from_pcb.
        if (nodeRegenerateRequired) {
            const excludeFromPcb = ElementHelper.isExcludedFromPcb(subject.properties); // Important that subject.properties has been set above so we're using the new value if changed.
            const excludeFromPcbWasChanged = Object.values(propertyUpdates).some((p) => p.name === defaultPropertyDefinitions.exclude_from_pcb.label);
            if (excludeFromPcbWasChanged && excludeFromPcb) {
                // If user has changed it so the node is excluded from PCB, remove it from PCB object tree.
                removeNodesAndDescendants(draftState.pcbLayoutNodes, [subjectUid]);
            }
            if (!excludeFromPcb) {
                if (subjectType === "elements") {
                    setElementsAndGenerateNodes(draftState, [draftState.elements[subjectUid]]); // TODO: Fix '!'
                }
                // QUESTION: how to add only the nodes from the
                // subjectUid? or do we need this in case the element is
                // a layout or footprint?
                const pcbDomTreeManager = new PcbTreeManager(draftState.pcbLayoutNodes, logger);
                pcbDomTreeManager.updateNetNodes(draftState);
            }
        }
    }
}
export function handleSetDocumentProperties(draftState, properties) {
    const draftProperties = draftState.properties;
    for (const property of Object.values(properties)) {
        draftProperties[property.uid] = property;
    }
}
export function handleRemoveDocumentProperties(draftState, propertyUids) {
    for (const propertyUid of propertyUids) {
        delete draftState.properties[propertyUid];
    }
}
export function doDeleteSubjects(draftState, subjectUids, logger, vertexUpdates, deselectUids = []) {
    const { elements: oldElements, pcbLayoutRuleSets: oldPcbLayoutRuleSets } = draftState;
    const elementsToRemove = filterTruthy(subjectUids.map((uid) => oldElements[uid]));
    /**
     * Usually an element has a corresponding pcb node with the same uid, so we want
     * to remove that corresponding node sub-tree. However, there is an exception:
     * - GND terminal has a different uid because we generate a net node for it
     *
     * Excluding the exception so that we are not trying to remove an non-existed node
     */
    const nodeUidsToRemove = elementsToRemove
        .filter((element) => !isGroundElement(element))
        .map((element) => element.uid);
    removeNodesAndDescendants(draftState.pcbLayoutNodes, nodeUidsToRemove);
    for (const element of elementsToRemove) {
        delete oldElements[element.uid];
        const nodeSelector = PcbLayoutRuleCompiler.createUidAttributeSelector(PcbNodeTypes.element, element.uid);
        const ruleSet = PcbLayoutRuleCompiler.getRuleSetBasedOnSelector(nodeSelector, oldPcbLayoutRuleSets);
        if (ruleSet) {
            delete oldPcbLayoutRuleSets[ruleSet.uid];
        }
    }
    // See comment above `actions.ts:deleteSubjects` for why we pass in `deselectUids`
    // as a separate list to `subjectUids`.
    removeSubjectsFromSelection(draftState, deselectUids);
    if (!vertexUpdates) {
        return;
    }
    for (const update of vertexUpdates) {
        applyChange(draftState.routeVertices, true, update);
    }
    draftState.elements = oldElements;
    if (vertexUpdates.length > 0) {
        const pcbDomTreeManager = new PcbTreeManager(draftState.pcbLayoutNodes, logger);
        pcbDomTreeManager.updateNetNodes(draftState, { deleteNodesOnly: true });
    }
}
function setPcbLayoutRuleOrder(pcbLayoutRule, existingRulesCount) {
    return create(pcbLayoutRule, (draft) => {
        if (draft.order === undefined) {
            draft.order = existingRulesCount + 1;
        }
    });
}
export function handleSetNodeRules(draftState, updatedNodes) {
    for (const updatedNode of Object.values(updatedNodes)) {
        const node = draftState.pcbLayoutNodes[updatedNode.nodeUid];
        if (node) {
            node.pcbNodeRuleSet ??= {};
            for (const rule of Object.values(updatedNode.updatedPcbLayoutRules)) {
                node.pcbNodeRuleSet[rule.key] = setPcbLayoutRuleOrder(rule, Object.keys(node.pcbNodeRuleSet).length);
            }
        }
    }
}
//# sourceMappingURL=reducer.js.map